import { post } from 'axios';
import { getFormattedPrice, mathRound } from 'Components/ContractGenerator/shared/helpers/priceHelpers';
import PropTypes from 'prop-types';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { contractGeneratorSelectors } from 'Redux/ducks/contractGenerator';
import useDeepCompareEffect from 'use-deep-compare-effect';
import Alert from 'ViatoUi/Extranet/Surfaces/Alert';
import Badge from 'ViatoUi/Shared/DataDisplay/Badge';
import { FlexBox } from 'ViatoUi/Shared/Layouts/Flex';
import Spacer from 'ViatoUi/Shared/Layouts/Spacer';
import Loader from 'ViatoUi/Shared/Loader';
import { Headline, Paragraph } from 'ViatoUi/Shared/Typo';

import styles from './PriceSummary.module.scss';

const PriceSummary = ({ headline, showFinalPrice }) => {
	const urlPrefix = process.env.NODE_ENV !== 'production' ? '' : 'https://contract-generator.viato.travel';
	const { t } = useTranslation('contractGenerator');

	const queryString = window.location.search;
	const urlParams = new URLSearchParams(queryString);
	const sessionId = urlParams.get('cgs');

	const windowWidth = useSelector(contractGeneratorSelectors.getWindowWidth);
	const selectedRooms = useSelector(contractGeneratorSelectors.getSelectedRooms);
	const selectedProduct = useSelector(contractGeneratorSelectors.getSelectedProduct);
	const selectedExtensions = useSelector(contractGeneratorSelectors.getSelectedExtensions);
	const selectedContractTermInMonths = useSelector(contractGeneratorSelectors.getSelectedContractTermInMonths);
	const selectedPaymentType = useSelector(contractGeneratorSelectors.getSelectedPaymentType);

	const [finalPrices, setFinalPrices] = useState({
		monthly: {
			full: null,
			discounted: null
		},
		single: {
			full: null,
			discounted: null
		}
	});

	const finalTotalPrice =
		mathRound(finalPrices.single.discounted) + mathRound(finalPrices.monthly.discounted) * selectedContractTermInMonths;

	const finalTotalPriceWithoutDiscount =
		mathRound(finalPrices.single.full) + mathRound(finalPrices.monthly.full) * selectedContractTermInMonths;

	const priceDifference = mathRound(finalTotalPriceWithoutDiscount) - mathRound(finalTotalPrice);

	const priceDiffernecePercentage = mathRound(
		((finalTotalPrice - finalTotalPriceWithoutDiscount) / finalTotalPriceWithoutDiscount) * 100 * -1
	);

	useDeepCompareEffect(() => {
		setFinalPrices({
			monthly: {
				full: null,
				discounted: null
			},
			single: {
				full: null,
				discounted: null
			}
		});
		post(`${urlPrefix}/api/prices/finalPrices`, {
			selectedRooms,
			selectedProduct,
			selectedExtensions,
			selectedContractTermInMonths,
			selectedPaymentType,
			session: sessionId || 'noSession'
		}).then(response => setFinalPrices(response.data));
	}, [selectedRooms, selectedProduct, selectedExtensions, selectedContractTermInMonths, selectedPaymentType]);

	const getPriceContent = (defaultPrice, discountedPrice) => {
		if (defaultPrice > discountedPrice) {
			const percentualDifference = mathRound(((discountedPrice - defaultPrice) / defaultPrice) * 100 * -1);

			return (
				<>
					<Paragraph color='darkSmoke' lineThrough>
						{getFormattedPrice(defaultPrice)}&nbsp;€*
					</Paragraph>
					<Spacer width='s' />
					<Headline className={styles.headline} type={windowWidth > 810 ? 'h3' : 'h4'}>
						{getFormattedPrice(discountedPrice)}&nbsp;€*
					</Headline>
					{windowWidth > 810 && (
						<>
							<Spacer width='s' />
							<Badge label={`≈ -${percentualDifference}%`} size='s' type='error' inline />
						</>
					)}
				</>
			);
		}

		return (
			<Headline className={styles.headline} type={windowWidth > 810 ? 'h3' : 'h4'}>
				{getFormattedPrice(defaultPrice)}&nbsp;€*
			</Headline>
		);
	};

	return (
		<div>
			{headline}
			<FlexBox className={styles.prices}>
				{finalPrices.single.full === null ? (
					<>
						<FlexBox alignItems='baseline' className={styles.price}>
							<Paragraph color='darkSmoke'>{t('layouts.priceSummary.once')}:</Paragraph>
							<Spacer width='s' />
							<Loader iconOnly />
						</FlexBox>
						{windowWidth > 810 && <Spacer width='l' />}
						<FlexBox alignItems='baseline' className={styles.price}>
							<Paragraph color='darkSmoke'>{t('layouts.priceSummary.monthly')}:</Paragraph>
							<Spacer width='s' />
							<Loader iconOnly />
						</FlexBox>
					</>
				) : (
					<>
						<FlexBox alignItems='baseline' className={styles.price}>
							<Paragraph color='darkSmoke'>{t('layouts.priceSummary.once')}:</Paragraph>
							<Spacer width='s' />
							{getPriceContent(finalPrices.single.full, finalPrices.single.discounted)}
						</FlexBox>
						{windowWidth > 810 && <Spacer width='l' />}
						<FlexBox alignItems='baseline' className={styles.price}>
							<Paragraph color='darkSmoke'>{t('layouts.priceSummary.monthly')}:</Paragraph>
							<Spacer width='s' />
							{getPriceContent(finalPrices.monthly.full, finalPrices.monthly.discounted)}
						</FlexBox>
					</>
				)}
			</FlexBox>
			{showFinalPrice === true && (
				<>
					<Spacer height='l' />
					<Alert display='inline-block' type='info'>
						{t('layouts.priceSummary.totalPrice', {
							months: selectedContractTermInMonths,
							price: getFormattedPrice(finalTotalPrice)
						})}
						{priceDifference > 0 && (
							<>
								<br />
								<strong>
									{t('layouts.priceSummary.totalDiscount', {
										percent: priceDiffernecePercentage,
										price: getFormattedPrice(priceDifference)
									})}
								</strong>
							</>
						)}
					</Alert>
				</>
			)}
		</div>
	);
};

PriceSummary.propTypes = {
	headline: PropTypes.node.isRequired,
	showFinalPrice: PropTypes.bool
};

PriceSummary.defaultProps = {
	showFinalPrice: false
};

export default PriceSummary;
